import { Divider } from "@mui/material";
import { type PropsWithChildren, type ReactNode } from "react";
import { useIntl } from "react-intl";

import { AirlineAvatarGroup } from "./AirlineAvatarGroup";

type Airline = {
  name: string;
  logoUrl: string;
  code: string;
  flightNumber: string | null;
};

export type Props = PropsWithChildren<{
  topInfo?: ReactNode;
  bottomInfo?: ReactNode;
  onClick?: () => void;
  airlines: Airline[];
}>;

export function SingleAvailableFlight({
  topInfo,
  bottomInfo,
  onClick,
  airlines,
  children,
}: Props) {
  const intl = useIntl();

  const Component = onClick ? "button" : "div";

  return (
    <Component
      className="section-card flex w-full flex-col gap-4"
      {...(Component === "button" ? { onClick, type: "button" } : {})}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex w-fit">
            <AirlineAvatarGroup airlines={airlines} />
          </div>
          <div className="flex flex-wrap gap-1">
            {airlines.slice(0, 2).map((airline, index) => (
              <span
                key={airline.name || index}
                className="truncate text-sm text-text-primary"
              >
                {airline.name ||
                  intl.formatMessage({
                    id: "unknownAirline",
                    defaultMessage: "Unknown airline",
                  })}{" "}
                <span className="text-xs text-text-tertiary">
                  ({airline.code} {airline.flightNumber})
                </span>
                {airlines.length > index + 1 ? "," : ""}
              </span>
            ))}
            {airlines.length > 2 && (
              <span className="mr-1 text-sm text-text-primary">
                +{airlines.length - 2}
              </span>
            )}
          </div>
        </div>
        {topInfo ? (
          <div className="flex items-center gap-2">{topInfo}</div>
        ) : null}
      </div>

      {children}

      {bottomInfo ? (
        <>
          <Divider
            sx={{
              borderColor: "var(--color-stroke-neutral-softer)",
            }}
          />
          {bottomInfo}
        </>
      ) : null}
    </Component>
  );
}
